import { useCallback, useState } from "react";
import { api } from "../../services/api/api";
import { useWorkorderContext } from "../../context/WorkorderContext";
import { toast } from "react-hot-toast";
import { t } from "i18next";
import { createTextSearchQuery, searchWithFilters } from "../../services/api/search";

// Type pour les parts en mode édition/brouillon
export interface ExtendedWoPart extends Partial<EasyTech.WoPart> {
  isDraft?: boolean;
  isEditing?: boolean;
  id: string;
  part: any;
  quantity: number;
  price: number;
  desk: boolean;
  rate?: number;
  searchQuery?: string;
  purchasePrice?: number;
  originalPart?: any;
  documentReference?: string;
  createdReference?: boolean;
}

// Type édition d'une part
export interface PartEditData {
  searchQuery: string;
  part: any;
  quantity: number;
  rate: number;
  price: number;
  desk: boolean;
  purchasePrice?: number;
  originalPart?: any;
  documentReference?: string;
  createdReference?: boolean;
}

/**
 * La web n'a pas de parcours « reprendre une référence comptoir existante » : un comptoir saisi
 * ici est toujours une référence que l'utilisateur vient de créer. Une ligne venue du mobile
 * porte déjà sa valeur, qu'on respecte.
 */
export function isCreatedReference(part: { desk?: boolean; createdReference?: boolean }): boolean {
  if (!part.desk) return false;
  return part.createdReference ?? true;
}

// Type pour l'objet d'API
interface PartApiData {
  id: string;
  workOrder: { id: string };
  part: { id: string };
  machine?: { id: string };
  hose?: { id: string };
  quantity: number;
  rate: number;
  price: number;
  desk: boolean;
  purchasePrice?: number;
  documentReference?: string;
  createdReference?: boolean;
}

interface UseWorkorderPartsProps {
  workorderId: string;
  targetType?: "workorder" | "machine" | "hose";
  machineId?: string;
  hoseId?: string;
}

/**
 * Reporte sur une ligne affichée ce qui vient d'être enregistré.
 *
 * Une seule liste pour les quatre emplacements d'une ligne (intervention, machine, flexible,
 * flexible sur machine) : quand chacun avait la sienne, elles ont divergé, et le numéro de
 * document disparaissait de l'écran après une modification alors que le serveur l'avait gardé.
 */
function applyEdit<T extends object>(line: T, updatedData: PartEditData): T {
  return {
    ...line,
    part: updatedData.part,
    quantity: updatedData.quantity,
    rate: updatedData.rate,
    price: updatedData.price,
    desk: updatedData.desk,
    purchasePrice: updatedData.purchasePrice,
    documentReference: updatedData.documentReference,
    createdReference: updatedData.createdReference,
  };
}

/**
 * Hook pour gérer les pièces d'un workorder
 * @param props - Propriétés pour configurer le hook
 * @returns Fonctions pour manipuler les pièces
 */
export function useWorkorderParts({
  workorderId,
  targetType = "workorder",
  machineId,
  hoseId,
}: UseWorkorderPartsProps) {
  // État local pour la recherche de parts
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<EasyTech.Part[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const {
    addPartToWorkorder,
    addPartToMachine,
    addPartToHose,
    removePartFromWorkorder,
    removePartFromMachine,
    removePartFromHose,
    updatePart,
    workorder,
    updateWorkorder
  } = useWorkorderContext();

  /**
   * Fonction pour ajouter une pièce à une machine
   * @param machineId - ID de la machine
   * @param partData - Données de la pièce à ajouter
   * @returns La nouvelle pièce créée
   */
  const addMachinePart = useCallback(
    async (
      machineId: string,
      partData: Partial<EasyTech.WoPart>
    ): Promise<EasyTech.WoPart | null> => {
      try {
        // Préparer les données pour l'ajout à une machine
        const data = {
          workOrder: { id: workorderId },
          part: partData.part,
          quantity: partData.quantity,
          rate: partData.rate,
          desk: partData.desk,
          price: partData.price,
          purchasePrice: partData.purchasePrice,
          documentReference: partData.documentReference,
          createdReference: partData.createdReference ?? false,
          machine: { id: machineId }
        };

        // Route API pour ajouter une pièce à une machine
        const apiRoute = `users/workorders/${workorderId}/machine/${machineId}/part`;


        // Appel API
        const response = await api.post(apiRoute, data);
        const newPart = response as EasyTech.WoPart;


        if (partData.purchasePrice && !newPart.purchasePrice) {
          newPart.purchasePrice = partData.purchasePrice;
        }

        // Mettre à jour le contexte
        addPartToMachine(machineId, newPart);

        return newPart;
      } catch (error) {
        console.error("Erreur lors de l'ajout de la pièce à la machine:", error);
        return null;
      }
    },
    [workorderId, addPartToMachine]
  );

  /**
   * Ajoute une pièce au workorder, à une machine ou à un hose
   * @param partData - Données de la pièce à ajouter
   * @returns La nouvelle pièce créée
   */
  const addPart = useCallback(
    async (
      partData: Partial<EasyTech.WoPart>
    ): Promise<EasyTech.WoPart | null> => {
      try {

        if (targetType === "machine" && machineId) {
          return addMachinePart(machineId, partData);
        }

        // Préparer les données selon le type de cible
        const data: any = {
          workOrder: { id: workorderId },
          part: partData.part,
          quantity: partData.quantity,
          rate: partData.rate,
          desk: partData.desk,
          price: partData.price,
          purchasePrice: partData.purchasePrice,
          documentReference: partData.documentReference,
          createdReference: partData.createdReference ?? false,
        };

        let apiRoute = `users/workorders/${workorderId}/part`;

        // Si on ajoute à un hose
        if (targetType === "hose" && hoseId) {
          data.hose = { id: hoseId };

          // Si le hose est associé à une machine, ajouter aussi lobjet machine
          if (machineId) {
            data.machine = { id: machineId };
            apiRoute = `users/workorders/${workorderId}/machine/${machineId}/part`;
          }
        }

        const response = await api.post(apiRoute, data);
        const newPart = response as EasyTech.WoPart;


        if (partData.purchasePrice && !newPart.purchasePrice) {
          newPart.purchasePrice = partData.purchasePrice;
        }

        // Mise à jour du contexte uniquement si nécessaire
        if (targetType === "hose" && hoseId) {
          // Si le flexible existe déjà, simplement ajouter la pièce au flexible
          addPartToHose(hoseId, newPart);
        } else {
          addPartToWorkorder(newPart);
        }

        return newPart;
      } catch (error) {
        toast.error(t("workorder:parts:error:addPart"));
        return null;
      }
    },
    [
      workorderId,
      targetType,
      machineId,
      hoseId,
      addPartToWorkorder,
      addPartToHose,
      addMachinePart
    ]
  );

  /**
   * Supprime une pièce
   * @param partId - ID de la pièce à supprimer
   * @returns true si la suppression a réussi
   */
  const removePart = useCallback(
    async (partId: string, targetType: string, machineId: string, hoseId?: string): Promise<boolean> => {
      try {
        let apiRoute;

        // Déterminer la route API en fonction du type de cible
        if ((targetType === "machine" && machineId) || (targetType === "hose" && hoseId && machineId)) {
          // Route pour supprimer une pièce d'une machine
          apiRoute = `users/workorders/${workorderId}/machine/part/${partId}`;
        } else {
          // Route pour supprimer une pièce en dehors d'une machine (workorder ou hose)
          apiRoute = `users/workorders/${workorderId}/part/${partId}`;
        }

        await api.delete(apiRoute);

        // Mettre à jour le contexte selon le type de cible
        if (targetType === "machine" && machineId) {
          removePartFromMachine(machineId, partId);
        } else if (targetType === "hose" && hoseId) {
          removePartFromHose(hoseId, partId);
        } else {
          removePartFromWorkorder(partId);
        }

        return true;
      } catch (error) {
        toast.error(t("workorder:parts:error:removePart"));
        return false;
      }
    },
    [
      workorderId,
      removePartFromWorkorder,
      removePartFromMachine,
      removePartFromHose,
    ]
  );

  /**
   * Met à jour une pièce
   * @param partId - ID de la pièce à mettre à jour
   * @param partData - Nouvelles données de la pièce
   * @returns La pièce mise à jour
   */
  const editPart = useCallback(
    async (
      partId: string,
      partData: Partial<EasyTech.WoPart>
    ): Promise<EasyTech.WoPart | null> => {
      try {
        let apiRoute;

        // Déterminer la route API en fonction du type de cible
        if ((targetType === "machine" && machineId) || (targetType === "hose" && hoseId && machineId)) {
          // Route pour mettre à jour une pièce liée à une machine
          apiRoute = `users/workorders/${workorderId}/machine/part/${partId}`;
        } else if (targetType === "hose" && hoseId) {
          // Route pour mettre à jour une pièce liée à un hose (sans machine)
          apiRoute = `users/workorders/${workorderId}/part/${partId}`;
        } else {
          // Route pour mettre à jour une pièce du workorder
          apiRoute = `users/workorders/${workorderId}/part/${partId}`;
        }

        const response = await api.put(apiRoute, partData);
        const updatedPart = response as EasyTech.WoPart;

        // Mettre à jour le contexte selon le type de cible
        if (targetType === "machine" && machineId) {
          // Mettre à jour dans la machine
          updatePart(partId, updatedPart);
        } else if (targetType === "hose" && hoseId) {
          // Mettre à jour dans le hose
          updatePart(partId, updatedPart);
        } else {
          // Mettre à jour dans le workorder
          updatePart(partId, updatedPart);
        }

        return updatedPart;
      } catch (error) {
        toast.error(t("workorder:parts:error:editPart"));
        return null;
      }
    },
    [workorderId, targetType, machineId, hoseId, updatePart]
  );

  /**
   * Recherche de parts en fonction d'une query
   * @param query Texte de recherche
   */
  const searchParts = useCallback(async (query: string) => {
    if (query.trim().length < 3) {
      setSearchResults([]);
      setIsSearchLoading(false);
      return;
    }

    try {
      setIsSearchLoading(true);
      searchWithFilters<EasyTech.Part>(
        "parts",
        {
          paginate: { pageSize: 5, pageNumber: 0 },
          queries: [createTextSearchQuery(["ref", "label"], query.trim())]
        }
      ).then((ret) => {
        setSearchResults(ret.data as EasyTech.Part[]);
      });
    } catch (error) {
      console.error("Erreur recherche pièces", error);
      setSearchResults([]);
    } finally {
      setIsSearchLoading(false);
    }
  }, []);

  /**
   * Met à jour un part dans le contexte du workorder
   * @param partId ID de la part à mettre à jour
   * @param updatedData Données mises à jour
   */
  const updatePartInContext = useCallback((partId: string, updatedData: PartEditData) => {
    const updatedWorkorder = { ...workorder };
    let partUpdated = false;

    // Chercher et mettre à jour la part dans le contexte approprié
    if (targetType === "workorder") {
      // Mise à jour dans les parts du workorder
      updatedWorkorder.parts = updatedWorkorder.parts.map(p => {
        if (p.id === partId) {
          partUpdated = true;
          return applyEdit(p, updatedData);
        }
        return p;
      });
    } else if (targetType === "machine" && machineId) {
      // Mise à jour dans les parts d'une machine
      updatedWorkorder.machines = updatedWorkorder.machines.map(machine => {
        if (machine.id === machineId) {
          return {
            ...machine,
            parts: machine.parts.map(p => {
              if (p.id === partId) {
                partUpdated = true;
                return applyEdit(p, updatedData);
              }
              return p;
            })
          };
        }
        return machine;
      });
    } else if (targetType === "hose" && hoseId) {
      // Chercher dans les hoses du workorder
      updatedWorkorder.hoses = updatedWorkorder.hoses.map(hose => {
        if (hose.id === hoseId) {
          return {
            ...hose,
            parts: hose.parts.map(p => {
              if (p.id === partId) {
                partUpdated = true;
                return applyEdit(p, updatedData);
              }
              return p;
            })
          };
        }
        return hose;
      });

      // Si la part n'a pas été trouvée, chercher dans les hoses des machines
      if (!partUpdated) {
        updatedWorkorder.machines = updatedWorkorder.machines.map(machine => {
          return {
            ...machine,
            hoses: machine.hoses.map(hose => {
              if (hose.id === hoseId) {
                return {
                  ...hose,
                  parts: hose.parts.map(p => {
                    if (p.id === partId) {
                      partUpdated = true;
                      return applyEdit(p, updatedData);
                    }
                    return p;
                  })
                };
              }
              return hose;
            })
          };
        });
      }
    }

    // Mettre à jour le contexte avec le workorder modifié
    if (partUpdated) {
      updateWorkorder(updatedWorkorder);
    }
  }, [workorder, updateWorkorder, targetType, machineId, hoseId]);

  /**
   * Helper pour gérer la création ou mise à jour de la définition de pièce pour les pièces comptoir (Desk)
   */

  const handleDeskPartDefinition = useCallback(async (data: PartEditData | ExtendedWoPart): Promise<string | null> => {
    if (!data.desk) return data.part?.id;

    console.log("Processing desk part definition for:", data);

    // On s'assure que la ref commence par EXT_
    let ref = data.part?.ref || "";
    ref = ref.trim();

    if (!ref) {
      // Generate unique ref if empty to avoid backend error
      ref = `EXT_${Date.now()}`;
    } else if (!ref.startsWith("EXT_")) {
      ref = `EXT_${ref}`;
    }

    // Pour saveEditedPart, le label est dans searchQuery, sinon dans part.label
    const label = (data as PartEditData).searchQuery || data.part?.label || "Pièce comptoir";

    const payload = {
      ref: ref,
      label: label,
      price: data.price || 0, // Prix unitaire pour le desk, defaults to 0 to avoid NPE
      purchasePrice: (data as any).purchasePrice || 0
    };

    try {
      // Si c'est déjà une pièce EXT avec un ID, on met à jour la définition
      if (data.part?.id && data.part?.ref?.startsWith("EXT_")) {
        console.log("Updating existing desk part:", data.part.id);
        await api.put(`users/parts/${data.part.id}`, payload);
        return data.part.id;
      } else {
        // Sinon on crée une nouvelle pièce
        console.log("Creating new desk part definition");
        const res: any = await api.post(`users/parts`, payload);
        return res.id;
      }
    } catch (e) {
      console.error("Erreur création/maj part desk", e);
      toast.error(t("workorder:parts:error:partDefinition"));
      return null;
    }
  }, []);

  /**
   * Sauvegarde les modifications d'un part
   * @param editingPartId ID de la part en édition
   * @param editedPartData Données du part édité
   */
  const saveEditedPart = useCallback(async (editingPartId: string, editedPartData: PartEditData) => {
    try {
      if (!editingPartId || !editedPartData.part) {
        toast.error(t("Données invalides pour la mise à jour"));
        return false;
      }

      // Gestion spécifique Desk : Créer/Mettre à jour la définition de la pièce d'abord
      let targetPartId = editedPartData.part.id;
      if (editedPartData.desk) {
        const deskPartId = await handleDeskPartDefinition(editedPartData);
        if (!deskPartId) return false; // Erreur lors de la création/maj
        targetPartId = deskPartId;
      }

      // Créer un objet adapté à l'API
      const partToUpdate: PartApiData = {
        id: editingPartId,
        workOrder: { id: workorderId },
        part: { id: targetPartId }, // Utiliser le bon ID (potentiellement nouveau)
        quantity: editedPartData.quantity,
        rate: editedPartData.rate || 0,
        price: editedPartData.price,
        desk: editedPartData.desk,
        purchasePrice: editedPartData.purchasePrice,
        documentReference: editedPartData.documentReference,
        createdReference: editedPartData.createdReference ?? false
      };

      if (machineId) {
        partToUpdate.machine = { id: machineId };
      }

      if (hoseId) {
        partToUpdate.hose = { id: hoseId };
      }

      // Sélectionner la route API appropriée en fonction du contexte
      let apiEndpoint;

      if (partToUpdate.machine) {
        // Route spécifique pour les parts associées à une machine
        apiEndpoint = `users/workorders/${workorderId}/machine/part/${editingPartId}`;
      } else {
        // Route standard pour les parts associées directement au workorder
        apiEndpoint = `users/workorders/${workorderId}/part/${editingPartId}`;
      }

      // Note: On met à jour le lien WorkOrderPart. Si targetPartId a changé (Standard -> Desk),
      // le backend mettra à jour la référence vers la nouvelle pièce.
      await api.put(apiEndpoint, partToUpdate);

      // Mettre à jour le contexte avec les nouvelles données
      // Important : Si on a changé de pièce (ID différent), il faut mettre à jour l'objet complet
      const updatedDataForContext = {
        ...editedPartData,
        part: {
          ...editedPartData.part,
          id: targetPartId,
          ref: editedPartData.desk
            ? (editedPartData.part.ref.startsWith("EXT_") ? editedPartData.part.ref : `EXT_${editedPartData.part.ref}`)
            : editedPartData.part.ref,
          label: editedPartData.searchQuery
        }
      };

      updatePartInContext(editingPartId, updatedDataForContext);

      toast.success(t("workorder:parts:success:editPart"));
      return true;
    } catch (error) {
      toast.error(t("workorder:parts:error:editPart"));
      return false;
    }
  }, [workorderId, machineId, hoseId, updatePartInContext, handleDeskPartDefinition]);

  /**
   * Sauvegarde une nouvelle part (brouillon)
   * @param draftPart Données du brouillon
   */
  const saveDraftPart = useCallback(async (draftPart: ExtendedWoPart) => {
    console.log("Saving draft part:", draftPart);
    try {
      if (!draftPart.part) {
        console.warn("Draft part is missing part object");
        toast.error(t("workorder:parts:error:partInvalid"));
        return false;
      }

      // Gestion spécifique Desk
      let targetPartId = draftPart.part.id;
      if (draftPart.desk) {
        const deskPartId = await handleDeskPartDefinition(draftPart);
        if (!deskPartId) return false;
        targetPartId = deskPartId;
      }

      const partToAdd = {
        part: { ...draftPart.part, id: targetPartId },
        quantity: draftPart.quantity || 1,
        rate: draftPart.rate || 0,
        price: draftPart.price || 0,
        desk: draftPart.desk || false,
        purchasePrice: draftPart.purchasePrice,
        documentReference: draftPart.documentReference,
        createdReference: isCreatedReference(draftPart)
      };

      await addPart(partToAdd);

      toast.success(t("workorder:parts:success:addPart"));
      return true;
    } catch (error) {
      toast.error(t("workorder:parts:error:addPart"));
      return false;
    }
  }, [addPart, handleDeskPartDefinition]);

  return {
    addPart,
    addPartToHose,
    removePart,
    editPart,
    searchParts,
    saveEditedPart,
    saveDraftPart,
    updatePartInContext,
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    isSearchLoading
  };
}
